import { formatToDecimals } from '@lib/helpers/formatting';
import moment from 'moment';
import React from 'react';
import { TenderDataType } from './SummaryTenderModal';
import { useQuery } from '@tanstack/react-query';
import { getTenderClassifications, getTenderCriteria, getTenderProcedures } from '@lib/api';
import SummaryInfoList from './SummaryInfoList';
import SummaryInfoStatus from './SummaryInfoStatus';
import SummaryTenderModalCig from './SummaryTenderModalCig';
import MassiveUpload from '@components/form/upload/massiveUpload';

interface SummaryTenderModalInfoProps {
	tenderData: TenderDataType;
}

const SummaryTenderModalInfo: React.FC<SummaryTenderModalInfoProps> = ({ tenderData }) => {
	const makeOptionsFromApiRes = (apiRes: { data: { code: string; name: string }[] }) => {
		if (!apiRes) return undefined;
		return apiRes?.data.map((item) => ({
			value: item.code,
			label: item.name,
		}));
	};

	const { data: optionsProceduresData } = useQuery({
		queryKey: ['optionsProcedures'],
		queryFn: () => getTenderProcedures(),
		select: makeOptionsFromApiRes,
	});
	const selectedProcedure = optionsProceduresData?.filter((option) => option.value === tenderData.assignmentProcedureCode);

	const { data: optionsClassificationsData } = useQuery({
		queryKey: ['optionsClassifications'],
		queryFn: () => getTenderClassifications(),
		select: makeOptionsFromApiRes,
	});
	const selectedClassification = optionsClassificationsData?.filter((option) => option.value === tenderData.tenderClassification);

	const { data: optionsCriteriaData } = useQuery({
		queryKey: ['optionsCriteria'],
		queryFn: () => getTenderCriteria(),
		select: makeOptionsFromApiRes,
	});
	const selectedCriteria = optionsCriteriaData?.filter((option) => option.value === tenderData.assignmentCriteriaCode);

	const yesOrNoOptions = [
		{
			value: 1,
			label: 'SÌ',
		},
		{
			value: 0,
			label: 'NO',
		},
	];

	const selectedGazettePubIT = yesOrNoOptions.filter((option) => option.value === (tenderData.officialGazettePubItaly ?? null));
	const selectedGazettePubUE = yesOrNoOptions.filter((option) => option.value === (tenderData.officialGazettePubEurope ?? null));
	const selectedDedicated = yesOrNoOptions.filter((option) => option.value === (tenderData.dedicated ?? null));

	const items = [
		{
			label: 'Società',
			value: tenderData?.company?.name ?? 'N.D.',
		},
		{
			label: 'N. Procedura',
			value: tenderData?.tenderNumber ?? 'N.D.',
		},
		{
			label: 'Oggetto',
			value: tenderData?.subject ?? 'N.D.',
		},
		{
			label: 'Classificazione gara',
			value: selectedClassification?.[0]?.label ?? 'N.D.',
		},
		{
			label: 'Tipologia procedura',
			value: selectedProcedure?.[0]?.label ?? 'N.D.',
		},
		{
			label: 'Criterio di aggiudicazione',
			value: selectedCriteria?.[0]?.label ?? 'N.D.',
		},
		{
			label: 'Data indizione',
			value: tenderData?.callDate ? moment(tenderData.callDate).format('YYYY-MM-DD') : 'N.D.',
		},
		{
			label: 'Pubblicazione gazzetta (GURI)',
			value: selectedGazettePubIT?.[0]?.label ?? 'N.D.',
		},
		{
			label: 'Pubblicazione gazzetta (GUUE)',
			value: selectedGazettePubUE?.[0]?.label ?? 'N.D.',
		},
		{
			label: "Importo base d'asta",
			value: tenderData?.auctionImport ? `${formatToDecimals(tenderData?.auctionImport)} €` : 'N.D.',
		},
		{
			label: 'Importo complessivo gara',
			value: tenderData?.totalImport ? `${formatToDecimals(tenderData?.totalImport)} €` : 'N.D.',
		},
		{
			label: 'Importo aggiudicato',
			value: tenderData?.import ? `${formatToDecimals(tenderData?.import)} €` : 'N.D.',
		},
		{
			label: 'Sistema di qualificazione',
			value: tenderData?.qualificationSystem ?? 'N.D.',
		},
		{
			label: 'Dedicata',
			value: selectedDedicated?.[0]?.label ?? 'N.D.',
		},
	];

	return (
		<>
			<div className="summary_info-container">
				<SummaryInfoStatus entityLabel="Stato della procedura" checklistColor={tenderData.checkliststatus?.color} />

				<div>
					<h4>Anagrafica</h4>
					<SummaryInfoList items={items} />
				</div>
				<hr />
				<div>
					<h4>CIG</h4>

					<SummaryTenderModalCig tenderData={tenderData} />
				</div>
				<hr />
				<div>
					<h4>Contratti Aperti</h4>

					<MassiveUpload entityId={tenderData.id} isSummary />
				</div>
			</div>
		</>
	);
};

export default SummaryTenderModalInfo;
